import axios from 'axios'
import { isEmpty } from 'lodash'

import { rootStore } from '../provider/StoreProvider'
import { NotificationPermissionType } from '../store/notification'

import { toast } from './toast'

/**
 * updateToken 메소드용 업데이트 타입
 * - save: 현재 로그인한 계정에 기기로부터 전달받은 토큰을 저장합니다.
 * - remove: 기기로부터 전달받은 토큰을 모든 사용자 계정에서 제거합니다.
 */
type UpdateType = 'save' | 'remove'
type PostMessage = { postMessage: (message: string) => void }
interface CustomWindow extends Window {
  DownloadAttachment?: PostMessage
  UpdateToken?: PostMessage
  ClipboardCopy?: PostMessage
  CheckNotificationPermission?: PostMessage
  OpenAppSettings?: PostMessage
  CheckAppVersion?: PostMessage
  artipio?: {
    onUpdateToken: (message: UpdateType, token?: string) => void
    onCheckNotificationPermission: (permission: NotificationPermissionType) => void
    onCheckAppVersion: (json: string) => void
  }
}

const _window = window as CustomWindow

// jsChannel.ts
// Flutter 웹뷰에 자바스크립트채널이 존재하는지 확인하고 메시지를 전송(postMessage())합니다.

/**
 * URL에서 첨부파일을 다운로드해 기기에 저장합니다.
 * @param url 첨부파일 URL
 * @param filename 저장할 파일명(선택, 모바일에선 작동하지 않으며 웹에서만 작동함)
 */
export async function downloadAttachment(url: string, filename?: string) {
  if (_window?.DownloadAttachment) {
    try {
      _window.DownloadAttachment.postMessage(url)
      return true
    } catch (error) {
      toast('파일 다운로드에 실패했습니다.', error)
      return false
    }
  }

  try {
    const link = document.createElement('a')

    link.href = url
    link.download = filename || String(url.split('/').pop())
    document.body.appendChild(link)
    link.click()

    URL.revokeObjectURL(link.href)
    link.parentNode?.removeChild(link)
    return true
  } catch (error) {
    toast('파일 다운로드에 실패했습니다.', error)
    return false
  }
}

/**
 * 기기에 FCM Token을 요청하고 전달받은 토큰을 사용자에게 등록하거나 모든 계정에서 제거합니다
 * 기기는 요청을 받으면 'onUpdateToken' 함수를 실행합니다.
 * - save: 현재 로그인한 계정에 기기로부터 전달받은 토큰을 저장합니다.
 * - remove: 기기로부터 전달받은 토큰을 모든 사용자 계정에서 제거합니다.
 * @param updateType {@link UpdateType}: save | remove
 */
export function updateToken(updateType: UpdateType) {
  try {
    _window?.UpdateToken?.postMessage(updateType)
  } catch (error) {
    // console.log('updateToken: ', { error, updateType })
  }
}

/**
 * 복사 기능 util 함수
 * @param text string - 클립보드에 복사 할 문구
 * @param message string - 복사 성공 메시지
 * @param isFooter boolean - footer 유무에 따른 여백 기준
 * @returns
 */
export async function clipboardCopy(
  text: string,
  { message = '클립보드에 복사되었어요.', isFooter = false }: { message?: string; isFooter?: boolean },
) {
  try {
    // ClipboardCopy라는 flutter js channel이 있으면 복사 시도
    if (_window?.ClipboardCopy) {
      try {
        _window.ClipboardCopy.postMessage(text)
        toast(message, undefined, isFooter)
      } catch (error) {
        toast('클립보드 복사에 실패했어요.', error, isFooter)
        console.log({ error })
      }
      return
    }

    const textarea = document.createElement('textarea')
    textarea.value = text // 복사할 메시지
    document.body.appendChild(textarea)
    textarea.select()
    textarea.setSelectionRange(0, 9999) // For IOS
    document.execCommand('copy')
    document.body.removeChild(textarea)

    toast(message, undefined, isFooter)
  } catch (error) {
    console.log('error: ', { error })
    toast('클립보드 복사에 실패했어요.', error, isFooter)
  }
}

/**
 * 기기에 '알림 허용 여부 조회'를 요청합니다.
 * 알림 허용 여부 문자열('authorized', 'provisional', 'denied', 'notDetermined', 'unknown' 중 하나)이 전달됩니다.
 * => 기기는 승인 여부를 확인 한 뒤 'onCheckNotificationPermission'를 실행합니다.
 */
export function checkNotificationPermission() {
  try {
    if (typeof _window.CheckNotificationPermission === 'undefined') {
      throw new Error('not CheckNotificationPermission')
    }

    _window.CheckNotificationPermission?.postMessage('')
  } catch (error) {
    throw error
    // console.log('checkNotificationPermission: ', { error })
  }
}

/**
 * 기기에 '설정 열기'를 요청합니다.
 */
export function openAppSettings() {
  try {
    _window.OpenAppSettings?.postMessage('')
  } catch (error) {
    // console.log('openAppSettings: ', { error })
  }
}

/**
 * 기기에 '현재 설치된 앱 버전 정보'를 요청합니다.
 * 버전 정보와 관련된 내용이 JSON String으로 전달됩니다.
 * => 기기는 버전 정보를 조회 한 뒤 'onCheckAppVersion'를 실행합니다.
 */
export function checkAppVersion() {
  try {
    _window.CheckAppVersion?.postMessage('')
  } catch (error) {
    // console.log('CheckAppVersion: ', { error })
  }
}

// Flutter 자바스크립트채널 에서 사용하는 Global 함수
_window.artipio = {
  onUpdateToken: async (message, token) => {
    if (message === 'remove' && !isEmpty(token)) {
      await axios.post('/user/device/remove', { token })
      return
    }
    if (message === 'save' && !isEmpty(token)) {
      await axios.post('/user/device/save', { token })
      return
    }
  },
  onCheckNotificationPermission: (permission) => {
    rootStore.notificationStore.setPermission(permission)
    const event = new CustomEvent('checkDeviceNotification', { detail: permission })
    document.dispatchEvent(event)
  },
  onCheckAppVersion: (json) => {
    try {
      const info = JSON.parse(json)
      rootStore.commonStore.setAppVersion(String(info?.version ?? ''))
      rootStore.commonStore.setBuildNumber(String(info?.buildNumber ?? ''))
    } catch (error) {
      console.error('onCheckAppVersion: ', { error })
    }
  },
}

export type { UpdateType, CustomWindow }
